import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Clock, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MaskedLines, Reveal, SectionHeader } from "../components/Reveal";
import RfqForm from "../components/RfqForm";
import { CONTACT, FAQS, IMG } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    let mounted = true;
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((list) => mounted && setProducts(Array.isArray(list) ? list : []))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const cards = [
    { icon: Mail, label: "Email", value: CONTACT.email, hint: "Replies within 24 hours", testid: "contact-email-card" },
    { icon: Phone, label: "Phone & WhatsApp", value: CONTACT.phone, hint: `WhatsApp · ${CONTACT.whatsapp}`, testid: "contact-phone-card" },
    { icon: MapPin, label: "Head Office", value: CONTACT.address, hint: "Visits by appointment", testid: "contact-address-card" },
    { icon: Clock, label: "Working Hours", value: CONTACT.hours, hint: "Enquiries answered every business day", testid: "contact-hours-card" },
  ];

  return (
    <div data-testid="page-contact" className="bg-mist">
      <section className="relative overflow-hidden pt-[72px]">
        <div className="dot-grid pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pb-20 lg:pt-24">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-700">
              <span className="h-[1px] w-8 bg-emerald-700/40" />
              Contact & RFQ
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines
              delay={0.2}
              lines={[
                "Tell us what",
                <span key="y" className="italic text-forest">
                  you need shipped.
                </span>,
              ]}
            />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Share grades, volumes and your destination port — a priced, docs-ready offer comes
              back within 24 hours.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-section" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Info */}
          <div className="space-y-5 lg:col-span-5">
            {cards.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.07}>
                <div
                  data-testid={c.testid}
                  className="group flex items-start gap-5 rounded-3xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-forest/40 hover:shadow-[0_20px_44px_-20px_rgba(6,78,59,0.3)]"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sage text-forest transition-colors group-hover:bg-forest group-hover:text-white">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                      {c.label}
                    </p>
                    <p className="mt-1.5 text-sm font-semibold text-ink">{c.value}</p>
                    <p className="mt-1 text-xs text-slate-500">{c.hint}</p>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.3}>
              <a
                href={CONTACT.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="flex items-center justify-center gap-3 rounded-full bg-[#1FA855] px-7 py-4 text-sm font-semibold text-white transition-all hover:brightness-110"
              >
                <MessageCircle className="h-5 w-5" />
                Chat on WhatsApp
              </a>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_30px_70px_-40px_rgba(6,78,59,0.35)] sm:p-9">
              <h2 className="font-display text-2xl font-semibold text-ink">Request for Quotation</h2>
              <p className="mt-2 text-sm text-slate-500">
                Fields marked * are required. Everything else helps us quote sharper.
              </p>
              <div className="mt-7">
                <RfqForm products={products} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="contact-faq" className="bg-sage/60 py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeader overline="Common Questions" title="Before you write to us…" />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-forest/15" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-ink hover:text-forest sm:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Location strip */}
      <section data-testid="contact-map" className="relative overflow-hidden">
        <img src={IMG.crane} alt="Port at dusk" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/75" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-20 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-gold">
              Head Office & Port Desk
            </p>
            <p className="mt-3 max-w-xl font-display text-2xl font-semibold text-white sm:text-3xl">
              {CONTACT.address}
            </p>
          </div>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="contact-map-email-link"
            className="rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-white"
          >
            Email the Export Desk
          </a>
        </div>
      </section>
    </div>
  );
}
